import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#bindAutoplayGalleryHeight();
  }

  /**
   * Keep a mobile carousel as tall as the visible slide when a video autoplays.
   * The portrait video would otherwise set the flex row height for every slide.
   */
  #bindAutoplayGalleryHeight() {
    if (!this.querySelector('deferred-media[autoplay]')) return;

    const scroller = this.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const { signal } = this.#controller;
    const desktop = window.matchMedia('(min-width: 769px)');

    const apply = (media) => {
      if (desktop.matches) {
        scroller.style.removeProperty('height');
        return;
      }
      if (!(media instanceof HTMLElement)) return;

      const ratio = Number.parseFloat(getComputedStyle(media).getPropertyValue('--ratio'));
      const height = ratio > 0 ? scroller.clientWidth / ratio : media.getBoundingClientRect().height;
      scroller.style.height = `${height}px`;
    };

    const syncActive = () => {
      apply(this.querySelector('slideshow-slide[aria-hidden="false"] .product-media'));
    };

    this.addEventListener(
      'slideshow:select',
      (event) => {
        const media = event.detail?.slide?.querySelector('.product-media');
        if (media) apply(media);
        else syncActive();
      },
      { signal }
    );
    desktop.addEventListener('change', syncActive, { signal });
    window.addEventListener('resize', syncActive, { signal });
    requestAnimationFrame(syncActive);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    const featuredMediaId = event.detail.resource?.featured_media?.id;
    const slideshow = newMediaGallery.querySelector('slideshow-component');
    if (featuredMediaId && slideshow?.select) {
      slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
